import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  Card,
  Row,
  Col,
} from "react-bootstrap";
import { FaEdit, FaKey, FaToggleOn, FaToggleOff, FaPlus } from "react-icons/fa";
import { userService } from "../services/api";
import { toast } from "react-toastify";

const UsuariosList = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    crewID: "",
    password: "",
    rol: "usuario",
  });

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      const response = await userService.getAll();
      setUsuarios(response.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedUser) {
        await userService.update(selectedUser.id, formData);
        toast.success("Usuario actualizado exitosamente");
      } else {
        await userService.create(formData);
        toast.success("Usuario creado exitosamente");
      }
      setShowModal(false);
      resetForm();
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error al guardar usuario");
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    try {
      await userService.changePassword(selectedUser.id, formData.password);
      toast.success("Contraseña actualizada exitosamente");
      setShowPasswordModal(false);
      resetForm();
    } catch (error) {
      toast.error("Error al cambiar contraseña");
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await userService.toggleStatus(userId);
      toast.success("Estado actualizado exitosamente");
      loadUsuarios();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const editUser = (user) => {
    setSelectedUser(user);
    setFormData({
      nombre: user.nombre,
      telefono: user.telefono || "",
      crewID: user.crewID,
      password: "",
      rol: user.rol,
    });
    setShowModal(true);
  };

  const changePassword = (user) => {
    setSelectedUser(user);
    setFormData({ password: "" });
    setShowPasswordModal(true);
  };

  const resetForm = () => {
    setSelectedUser(null);
    setFormData({
      nombre: "",
      telefono: "",
      crewID: "",
      password: "",
      rol: "usuario",
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (loading) {
    return <div className="text-center">Cargando...</div>;
  }

  return (
    <div>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Gestión de Usuarios</h5>
          <Button
            variant="primary"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <FaPlus /> Nuevo Usuario
          </Button>
        </Card.Header>
        <Card.Body>
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Crew ID</th>
                <th>Teléfono</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Fecha Creación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((user) => (
                <tr key={user.id}>
                  <td>{user.nombre}</td>
                  <td>{user.crewID}</td>
                  <td>{user.telefono || "-"}</td>
                  <td>
                    <Badge bg={user.rol === "admin" ? "danger" : "primary"}>
                      {user.rol}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={user.activo ? "success" : "danger"}>
                      {user.activo ? "Activo" : "Inactivo"}
                    </Badge>
                  </td>
                  <td>{new Date(user.fecha_creacion).toLocaleDateString()}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => editUser(user)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="me-2"
                      onClick={() => changePassword(user)}
                    >
                      <FaKey />
                    </Button>
                    <Button
                      variant={
                        user.activo ? "outline-success" : "outline-danger"
                      }
                      size="sm"
                      onClick={() => handleToggleStatus(user.id)}
                    >
                      {user.activo ? <FaToggleOn /> : <FaToggleOff />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {/* Leyenda informativa */}
          <div className="mt-3">
            <Card className="mb-0">
              <Card.Body>
                <h6 className="mb-2">Información de administradores plataforma</h6>
                <p className="mb-2">
                  Los administradores de plataforma pueden gestionar <strong>gastos</strong> y <strong>jornadas</strong> de todos los empleados desde la app (Gestión de usuarios). Para altas y ediciones, indique el <strong>teléfono con prefijo internacional</strong> (p. ej., 34 para España).
                </p>
                <p className="mb-1"><strong>Leyenda de acciones</strong>:</p>
                <div className="d-flex flex-column gap-1">
                  <div>
                    <span className="badge bg-primary me-2"><FaPlus /> Nuevo Usuario</span>
                    Crea un usuario con acceso a la plataforma.
                  </div>
                  <div>
                    <span className="badge bg-warning text-dark me-2"><FaEdit /> Editar</span>
                    Modifica nombre, teléfono, Crew ID o rol del usuario.
                  </div>
                  <div>
                    <span className="badge bg-warning text-dark me-2"><FaKey /> Cambiar contraseña</span>
                    Actualiza la contraseña del usuario seleccionado.
                  </div>
                  <div>
                    <span className="badge bg-success me-2"><FaToggleOn /></span>
                    <span className="badge bg-danger me-2"><FaToggleOff /></span>
                    Activa o desactiva el acceso del usuario a la plataforma.
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Card.Body>
      </Card>

      {/* Modal para crear/editar usuario */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedUser ? "Editar Usuario" : "Nuevo Usuario"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Crew ID *</Form.Label>
                  <Form.Control
                    type="text"
                    name="crewID"
                    value={formData.crewID}
                    onChange={handleChange}
                    required
                    disabled={!!selectedUser}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    type="text"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Rol *</Form.Label>
                  <Form.Select
                    name="rol"
                    value={formData.rol}
                    onChange={handleChange}
                    required
                  >
                    <option value="usuario">Usuario</option>
                    <option value="admin">Administrador</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {!selectedUser && (
              <Form.Group className="mb-3">
                <Form.Label>Contraseña *</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {selectedUser ? "Actualizar" : "Crear"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para cambiar contraseña */}
      <Modal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Cambiar Contraseña</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePasswordChange}>
          <Modal.Body>
            <p>
              Cambiar contraseña para: <strong>{selectedUser?.nombre}</strong>
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Nueva Contraseña *</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowPasswordModal(false)}
            >
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Cambiar Contraseña
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default UsuariosList;
